import { AbstractSerializer } from '../../../common/AbstractSerializer';
import type { BinaryReader } from '../../../common/BinaryReader';
import Header from '../../../common/Header.js';
import { initWorker } from '../../../common/WorkerUtility.js';
import type { LayerRecord, SerializerResolver } from '@visualizer/types';

export default class RttSerializer extends AbstractSerializer<void> {
    public readonly version: string;
    private readonly fileVersion: string;

    constructor(version: string) {
        super();
        this.version = version;
        this.fileVersion = version;
    }

    public async read(reader: BinaryReader, layer: LayerRecord, sm: SerializerResolver): Promise<void> {
        const worker = await initWorker();
        if (layer.raysHandler) {
            layer.raysHandler.loadingStatus = 'loading';
        }
        worker.onerror = (event: ErrorEvent) => {
            console.error('Worker error: ', event.message);
            if (layer.raysHandler) {
                layer.raysHandler.loadingStatus = 'error';
            }
            return true;
        };
        worker.onmessage = (e: MessageEvent<Record<string, any>>) => {
            if (e.data.rays != null) {
                layer.raysHandler?.init?.(e.data.rays, layer, 0);
            }

            if (e.data.paramsStatus != null) {
                layer.raysHandler?.addParams?.(e.data.paramsStatus, e.data.params1, e.data.params2);
            }

            if (e.data.spID != null) {
                const { frameID, spID, values, indices } = e.data;
                layer.raysHandler?.addRays?.(frameID, spID, indices, values);
            }
            if (e.data.finished === true) {
                if (layer.raysHandler) {
                    layer.raysHandler.loadingStatus = 'loaded';
                    console.log('Rays have been loaded in all the Frames and ready to use', layer.raysHandler);
                }
                worker.terminate();
            }
        };

        const headerSerializer = sm.findSerializer(this.fileVersion, Header, 'binary');

        worker.postMessage({ data: reader.getData(), kind: 'rtt', serializerVersion: headerSerializer.serializerVersion }, [
            reader.getData(),
        ]);
        console.log({ data: reader.getData(), kind: 'rtt' });
    }
}

export function loadRTT(generalLoader: BinaryReader, sensorPointsN: number, postMessage: (payload: Record<string, unknown>) => void): void {
    const numFrames = generalLoader.readUInt8();
    const numRays = generalLoader.readUInt32();

    const rays = new Array(numRays);

    for (let r = 0; r < numRays; r++) {
        const ray = generalLoader.readVec3();
        rays[r] = ray;
    }
    postMessage({ rays });

    const hasParams = generalLoader.readUInt8();

    if (hasParams > 0) {
        const params1: number[] = [];
        const params2: number[] = [];
        for (let r = 0; r < numRays; r++) {
            params1[r] = generalLoader.readFloat32();
            params2[r] = generalLoader.readFloat32();
        }

        postMessage({
            paramsStatus: 'done',
            params1,
            params2,
        });
    } else {
        postMessage({ paramsStatus: 'compute' });
    }

    for (let f = 0; f < numFrames; f++) {
        generalLoader.readString();
        const isEqual = generalLoader.readBoolean();
        let R1 = 0;
        let indices: number[] = [];
        for (let N = 0; N < sensorPointsN; N++) {
            if (!isEqual || N === 0) {
                R1 = generalLoader.readUInt32();
            }
            const values: number[] = [];
            for (let iL = 0; iL < R1; iL++) {
                values[iL] = generalLoader.readFloat32();
            }

            if (isEqual && N > 0) {
                postMessage({ frameID: f, spID: N, R1, indices, values });
                continue;
            }

            const isContinue = generalLoader.readBoolean();
            indices = [];
            if (isContinue) {
                const a = generalLoader.readUInt32();
                const b = a + R1;
                let cnt = 0;
                for (let i = a; i < b; i++) {
                    indices[cnt] = i;
                    cnt++;
                }
            } else {
                for (let iL = 0; iL < R1; iL++) {
                    indices[iL] = generalLoader.readUInt32();
                }
            }

            postMessage({ frameID: f, spID: N, R1, indices, values });
        }
    }
}
